import { useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { 
  Mic, 
  Play, 
  Square, 
  SkipBack, 
  SkipForward, 
  X, 
  Volume2,
  Pause,
  Repeat1,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { FuriganaText, SegmentedJapaneseText } from "./SubtitleTextComponents";
import { WordPopup, lookupWord, WordInfo } from "./WordPopup";
import { SubtitleLine } from "@/types/subtitle";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";

const IOS_SAFE_AUDIO_MIME_TYPES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
] as const;

const MIN_RECORDING_SECONDS = 0.8;

function canPlayAudioType(mimeType: string | undefined) {
  if (!mimeType || typeof window === "undefined") return false;
  const audio = document.createElement("audio");
  const direct = audio.canPlayType(mimeType);
  if (direct === "probably" || direct === "maybe") return true;

  const base = mimeType.split(";")[0];
  if (!base) return false;
  const baseResult = audio.canPlayType(base);
  return baseResult === "probably" || baseResult === "maybe";
}

function pickSupportedAudioMimeType() {
  if (typeof window === "undefined") return undefined;
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const mime of IOS_SAFE_AUDIO_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported?.(mime) && canPlayAudioType(mime)) return mime;
  }
  return undefined;
}

function getRecordingErrorText(error: unknown) {
  const err = error as DOMException | null | undefined;
  if (!err) {
    return "录音失败，请重试";
  }

  switch (err.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "你拒绝了麦克风权限，请在浏览器设置中允许后重试";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "未检测到麦克风设备";
    case "NotReadableError":
    case "AbortError":
      return "麦克风正被其他应用占用，请关闭后重试";
    case "SecurityError":
      return "当前环境不允许录音，请确认使用 HTTPS 访问";
    default:
      return err.message ? `录音失败：${err.message}` : "录音失败，请重试";
  }
}

interface ShadowingPanelProps {
  isVisible: boolean;
  onClose: () => void;
  currentSubtitle: SubtitleLine | null;
  onPrev: () => void;
  onNext: () => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onPause: () => void;
  currentTime?: number;
  
  // Loop control
  isLooping?: boolean;
  onToggleLoop?: () => void;

  // Subtitle rendering props
  showFurigana: boolean;
  showRomaji: boolean;
  showChinese: boolean;
  hideUnderline: boolean;
  hideGrammarBackground: boolean;
  hideShadowingSpotlight?: boolean;
  showOnlyChinese?: boolean;
  highlightNewWords: boolean;
  vocabulary: any;
}

export function ShadowingPanel({
  isVisible,
  onClose,
  currentSubtitle,
  onPrev,
  onNext,
  isPlaying,
  onTogglePlay,
  onSeek,
  onPause,
  currentTime = 0,
  showFurigana,
  showRomaji,
  showChinese,
  hideUnderline,
  hideGrammarBackground,
  hideShadowingSpotlight = false,
  showOnlyChinese = false,
  highlightNewWords,
  vocabulary,
  isLooping = false,
  onToggleLoop,
  tokenizer,
}: ShadowingPanelProps & { tokenizer?: any }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecorderBusy, setIsRecorderBusy] = useState(false);
  const [viewMode, setViewMode] = useState<"japanese" | "chinese" | "both">("both");
  
  // Dynamic font sizing based on content length
  const textSizeClass = useMemo(() => {
    if (!currentSubtitle?.japanese) return "text-2xl md:text-3xl lg:text-4xl";
    const len = currentSubtitle.japanese.length;
    // Keep text readable across phone/tablet/desktop and avoid clipping in short-height windows.
    if (len <= 10) return "text-2xl sm:text-3xl lg:text-4xl";
    if (len <= 20) return "text-xl sm:text-2xl lg:text-3xl";
    if (len <= 40) return "text-lg sm:text-xl lg:text-2xl";
    if (len <= 70) return "text-base sm:text-lg lg:text-xl";
    return "text-sm sm:text-base lg:text-lg";
  }, [currentSubtitle]);

  // Word popup state
  const [popupWord, setPopupWord] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [hoverWord, setHoverWord] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<string>("未知");
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const hoverTimerRef = useRef<number | null>(null);

  const { user } = useAuth();
  const { toast } = useToast();
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recorderMimeTypeRef = useRef<string | undefined>(undefined);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingSessionRef = useRef(0);
  const isRecordingRef = useRef(false);
  const isRecorderBusyRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RECORDING_TIME = 15; // seconds
  const isAppleTouchDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const touchPoints = Number(window.navigator.maxTouchPoints || 0);
    const isIOS = /iPad|iPhone|iPod/i.test(ua);
    const isIpadOSDesktopUA = /Mac/i.test(platform) && touchPoints > 1;
    const isAppleWebkitTouch =
      /AppleWebKit/i.test(ua) && touchPoints > 1 && /Mobile|CriOS|FxiOS|EdgiOS/i.test(ua);
    return isIOS || isIpadOSDesktopUA || isAppleWebkitTouch;
  }, []);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isRecorderBusyRef.current = isRecorderBusy;
  }, [isRecorderBusy]);

  const setRecorderBusyState = (next: boolean) => {
    isRecorderBusyRef.current = next;
    setIsRecorderBusy(next);
  };

  const clearRecordingTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopMediaStream = (targetStream?: MediaStream | null) => {
    const stream = targetStream ?? streamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // Ignore stream stop errors.
      }
    });
    if (!targetStream || targetStream === streamRef.current) {
      streamRef.current = null;
    }
  };

  const resetRecordingRuntime = () => {
    clearRecordingTimer();
    stopMediaStream();
    mediaRecorderRef.current = null;
    recorderMimeTypeRef.current = undefined;
    chunksRef.current = [];
    recordingStartedAtRef.current = null;
    setIsRecording(false);
    setRecorderBusyState(false);
  };

  // Clean up resources on unmount
  useEffect(() => {
    return () => {
      recordingSessionRef.current += 1;
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      resetRecordingRuntime();
    };
  }, [audioUrl]);

  // Vocabulary set for efficient lookup
  const vocabularySet = new Set((vocabulary || []).map((v: any) => v.word));

  useEffect(() => {
    if (isVisible) return;
    recordingSessionRef.current += 1;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // Ignore stop errors during panel close.
      }
    }
    resetRecordingRuntime();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlayingRecording(false);
  }, [isVisible]);

  const progress = currentSubtitle
    ? Math.min(Math.max((currentTime - currentSubtitle.startTime) / (currentSubtitle.endTime - currentSubtitle.startTime), 0), 1)
    : 0;

  const handleWordClick = (word: string, rect: DOMRect) => {
    setPopupWord(word);
    setPopupPosition({ x: rect.left, y: rect.bottom });
  };

  const handleWordHover = (word: string, rect: DOMRect) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverWord(word);
    setHoverPos("未知");
    setHoverPosition({ x: rect.left, y: rect.top });
    hoverTimerRef.current = window.setTimeout(async () => {
      try {
        const info = await lookupWord(word);
        setHoverPos(info.partOfSpeech || "未知");
      } catch {
        setHoverPos("未知");
      }
    }, 250);
  };

  const handleAddToVocabulary = async (info: WordInfo) => {
    if (!user) {
      toast({ title: "请先登录", variant: "destructive" });
      setPopupWord(null);
      return;
    }

    try {
      const { error } = await supabase.from("vocabulary").insert({
        user_id: user.id,
        word: info.word,
        reading: info.reading,
        romaji: info.romaji,
        meaning: info.meaning,
        chinese_translation: info.chineseTranslation || null,
        part_of_speech: info.partOfSpeech,
        example_sentence: info.example || null,
        // source_video_id: videoId || null, // We don't have videoId here easily, can pass it if needed
      });

      if (error) throw error;
      toast({ title: "已加入生词本" });
      setPopupWord(null);
    } catch (error) {
      console.error("Failed to add vocabulary:", error);
      toast({ title: "保存失败", variant: "destructive" });
    }
  };

  const startRecording = async () => {
    if (isRecorderBusyRef.current || isRecordingRef.current) return;
    if (!currentSubtitle) {
      toast({
        title: "请先等字幕出现",
        description: "当前句子还没开始，先播放到有字幕的位置再录音",
        variant: "destructive",
      });
      return;
    }
    const subtitleSpan = Math.max(0.01, currentSubtitle.endTime - currentSubtitle.startTime);
    const subtitleProgress = Math.max(
      0,
      Math.min(1, (currentTime - currentSubtitle.startTime) / subtitleSpan),
    );
    if (subtitleProgress < 0.05 || subtitleProgress > 1.05) {
      toast({
        title: "请在当前句子播放中录音",
        description: "等待这一句字幕开始后再点录音，稳定性更高",
        variant: "destructive",
      });
      return;
    }
    try {
      if (typeof window === "undefined" || !window.isSecureContext) {
        toast({
          title: "无法录音",
          description: "请使用 HTTPS 访问后重试",
          variant: "destructive",
        });
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        toast({
          title: "设备不支持录音",
          description: "当前浏览器不支持麦克风录音",
          variant: "destructive",
        });
        return;
      }

      if (typeof MediaRecorder === "undefined") {
        toast({
          title: "设备不支持录音",
          description: "Safari 版本过低，请升级系统或浏览器",
          variant: "destructive",
        });
        return;
      }

      const nextSessionId = recordingSessionRef.current + 1;
      recordingSessionRef.current = nextSessionId;

      // Pause original media while recording to avoid Safari resource conflicts.
      onPause();
      // iOS Safari may need a short settle time after pausing long video playback.
      if (isAppleTouchDevice) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 220);
        });
      }

      // Reset stale recorder/stream state before creating a new recording session.
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          // ignore
        }
      }
      resetRecordingRuntime();

      setRecorderBusyState(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      if (recordingSessionRef.current !== nextSessionId) {
        stopMediaStream(stream);
        return;
      }
      streamRef.current = stream;

      const previousUrl = audioUrl;
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
        setAudioUrl(null);
      }
      if (isPlayingRecording && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlayingRecording(false);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }

      // iPad/iPhone Safari is unstable with explicit mimeType in some versions.
      // Let browser choose its native recorder format on Apple touch devices.
      const preferredMimeType = isAppleTouchDevice ? undefined : pickSupportedAudioMimeType();
      recorderMimeTypeRef.current = preferredMimeType;
      const mediaRecorderOptions =
        preferredMimeType && MediaRecorder.isTypeSupported?.(preferredMimeType)
          ? { mimeType: preferredMimeType }
          : undefined;
      const mediaRecorder = mediaRecorderOptions
        ? new MediaRecorder(stream, mediaRecorderOptions)
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      recordingStartedAtRef.current = Date.now();

      mediaRecorder.ondataavailable = (e) => {
        if (recordingSessionRef.current !== nextSessionId) return;
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (recordingSessionRef.current !== nextSessionId) return;
        clearRecordingTimer();
        const outputTypeCandidates = [
          chunksRef.current[0]?.type,
          mediaRecorder.mimeType,
          recorderMimeTypeRef.current,
          "audio/mp4",
          "audio/webm",
          "audio/ogg",
        ].filter((item): item is string => !!item);
        const outputType = outputTypeCandidates.find((item) => canPlayAudioType(item));
        const blob = outputType
          ? new Blob(chunksRef.current, { type: outputType })
          : new Blob(chunksRef.current);
        const hasChunkData = chunksRef.current.some((chunk) => chunk.size > 0);
        if (!hasChunkData || blob.size === 0) {
          chunksRef.current = [];
          setAudioUrl(null);
          toast({
            title: "录音失败",
            description: "录音数据为空，请重新录制（建议保持1秒以上）",
            variant: "destructive",
          });
          stopMediaStream(stream);
          mediaRecorderRef.current = null;
          recorderMimeTypeRef.current = undefined;
          chunksRef.current = [];
          recordingStartedAtRef.current = null;
          setIsRecording(false);
          setRecorderBusyState(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        chunksRef.current = [];
        
        // Stop all tracks to release microphone
        stopMediaStream(stream);
        mediaRecorderRef.current = null;
        recordingStartedAtRef.current = null;
        setIsRecording(false);
        recorderMimeTypeRef.current = undefined;
        setRecorderBusyState(false);
      };

      mediaRecorder.onerror = (event) => {
        if (recordingSessionRef.current !== nextSessionId) return;
        console.error("MediaRecorder error:", event);
        clearRecordingTimer();
        stopMediaStream(stream);
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        recordingStartedAtRef.current = null;
        setIsRecording(false);
        recorderMimeTypeRef.current = undefined;
        setRecorderBusyState(false);
        toast({
          title: "录音中断",
          description: "录音过程中发生错误，请重试",
          variant: "destructive",
        });
      };

      // iOS Safari can crash with start(timeslice); use plain start() on Apple devices.
      if (isAppleTouchDevice) {
        mediaRecorder.start();
      } else {
        // Emit chunks periodically to reduce empty-blob edge cases on non-iOS browsers.
        mediaRecorder.start(250);
      }
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      clearRecordingTimer();
      timerRef.current = setInterval(() => {
        if (recordingSessionRef.current !== nextSessionId) return;
        const startedAt = recordingStartedAtRef.current;
        if (!startedAt) return;
        const elapsedSeconds = (Date.now() - startedAt) / 1000;
        setRecordingTime(elapsedSeconds);
        if (elapsedSeconds >= MAX_RECORDING_TIME) {
          stopRecording(true);
        }
      }, 100);
      setRecorderBusyState(false);
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      clearRecordingTimer();
      stopMediaStream();
      mediaRecorderRef.current = null;
      recorderMimeTypeRef.current = undefined;
      recordingStartedAtRef.current = null;
      setIsRecording(false);
      setRecorderBusyState(false);
      toast({
        title: "无法开始录音",
        description: getRecordingErrorText(err),
        variant: "destructive",
      });
    }
  };

  const stopRecording = (force = false) => {
    if (isRecorderBusyRef.current) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      const startedAt = recordingStartedAtRef.current;
      const elapsedSeconds = startedAt ? (Date.now() - startedAt) / 1000 : recordingTime;
      if (!force && elapsedSeconds < MIN_RECORDING_SECONDS) {
        toast({
          title: "录音太短",
          description: "请至少录制 1 秒再停止",
          variant: "destructive",
        });
        return;
      }
      setRecorderBusyState(true);
      clearRecordingTimer();
      if (!isAppleTouchDevice) {
        try {
          mediaRecorderRef.current.requestData?.();
        } catch {
          // ignore requestData unsupported errors
        }
      }
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error("Failed to stop recording:", error);
        resetRecordingRuntime();
        toast({
          title: "录音中断",
          description: "停止录音失败，请重试",
          variant: "destructive",
        });
      }
      setIsRecording(false);
      setIsPlayingRecording(false);
    } else if (streamRef.current) {
      resetRecordingRuntime();
    }
  };

  const toggleRecording = () => {
    if (isRecorderBusyRef.current) return;
    if (isRecordingRef.current) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  const playRecording = async () => {
    if (!audioUrl) return;
    if (isRecordingRef.current) {
      toast({
        title: "请先结束录音",
        description: "录音中无法试听，先点红色按钮停止录音",
        variant: "destructive",
      });
      return;
    }
    try {
      if (!audioRef.current) {
        const audio = new Audio();
        audio.preload = "auto";
        audio.onended = () => setIsPlayingRecording(false);
        (audio as HTMLAudioElement).playsInline = true;
        audio.setAttribute("playsinline", "true");
        audio.setAttribute("webkit-playsinline", "true");
        audioRef.current = audio;
      }

      audioRef.current.src = audioUrl;
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1;
      audioRef.current.muted = false;
      audioRef.current.load();
      await audioRef.current.play();
      setIsPlayingRecording(true);
    } catch (error) {
      console.error("Playback failed:", error);
      setIsPlayingRecording(false);
      const err = error as DOMException | undefined;
      const isNotAllowed = err?.name === "NotAllowedError";
      const isNotSupported = err?.name === "NotSupportedError" || err?.name === "AbortError";
      toast({
        title: "试听失败",
        description: isNotAllowed
          ? "当前浏览器限制了播放，请再次点击试听按钮"
          : isNotSupported
            ? "录音格式暂不可播放，请重新录一段（建议至少1秒）"
            : "录音播放失败，请重新录制后再试",
        variant: "destructive",
      });
    }
  };
  
  const pauseRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlayingRecording(false);
    }
  };

  if (!isVisible || typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Dimmed Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[240] transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Main Panel */}
      <div className="fixed inset-x-2 bottom-2 md:inset-x-4 md:bottom-4 lg:left-1/2 lg:right-auto lg:w-[min(76vw,1100px)] lg:-translate-x-1/2 h-[min(92dvh,760px)] md:h-[min(84dvh,760px)] bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl z-[250] animate-in slide-in-from-bottom-10 fade-in duration-300 rounded-[1.25rem] md:rounded-[1.75rem] overflow-hidden flex flex-col">
        {/* Header - Fixed to prevent scrolling away */}
        <div className="flex items-center justify-center p-3 sm:p-4 md:p-5 border-b border-border/10 relative shrink-0 bg-background/50 backdrop-blur-sm z-10">
            {/* View Mode Toggle - Left side */}
            <div className="absolute left-3 sm:left-4 md:left-5 top-1/2 -translate-y-1/2 hidden md:block">
              <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v as any)} className="bg-muted/50 rounded-full p-1 border border-border/20">
                <ToggleGroupItem value="japanese" className="rounded-full px-3 h-8 text-xs font-medium data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm">
                  日文
                </ToggleGroupItem>
                <ToggleGroupItem value="both" className="rounded-full px-3 h-8 text-xs font-medium data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm">
                  对照
                </ToggleGroupItem>
                <ToggleGroupItem value="chinese" className="rounded-full px-3 h-8 text-xs font-medium data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm">
                  中文
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="bg-primary/10 text-primary px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-medium shadow-sm flex items-center gap-2 border border-primary/20">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
              影子跟读模式
            </div>
            
            <Button type="button" variant="ghost" size="icon" onClick={onClose} className="absolute right-3 sm:right-4 md:right-5 top-1/2 -translate-y-1/2 rounded-full hover:bg-muted/50 h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 transition-colors">
              <X className="h-6 w-6" />
            </Button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto scrollbar-hide p-2.5 sm:p-4 md:p-6 pb-0 min-h-0">
            {/* Subtitle Display Container */}
            <div className="w-full h-full min-h-0 overflow-y-auto bg-muted/30 dark:bg-black/20 rounded-2xl md:rounded-[1.5rem] p-3 sm:p-4 md:p-7 flex flex-col items-center justify-center text-center space-y-3 sm:space-y-4 border border-border/10 shadow-inner transition-all duration-500">
              {currentSubtitle ? (
                <div className="flex flex-col items-center justify-center w-full h-full gap-4 animate-in fade-in zoom-in-95 duration-300">
                  {/* Japanese Text */}
                  {(viewMode === "japanese" || viewMode === "both") && (
                  <div className={cn(textSizeClass, "w-full max-w-full break-words [word-break:break-word] font-medium leading-relaxed tracking-wide select-none transition-all duration-300")}>
                     {currentSubtitle.furigana ? (
                      <FuriganaText 
                        segments={currentSubtitle.furigana}
                        japanese={currentSubtitle.japanese}
                        onWordClick={handleWordClick} 
                        onWordHover={handleWordHover}
                        active={true}
                        progress={progress}
                        showFurigana={showFurigana}
                        hideUnderline={hideUnderline}
                        hideGrammarBackground={hideGrammarBackground}
                        hideShadowingSpotlight={hideShadowingSpotlight}
                        highlightNewWords={highlightNewWords}
                        vocabularySet={vocabularySet}
                        tokenizer={tokenizer}
                      />
                    ) : (
                      <SegmentedJapaneseText
                        text={currentSubtitle.japanese}
                        onWordClick={handleWordClick}
                        onWordHover={handleWordHover}
                        active={true}
                        progress={progress}
                        showFurigana={showFurigana}
                        hideUnderline={hideUnderline}
                        hideGrammarBackground={hideGrammarBackground}
                        hideShadowingSpotlight={hideShadowingSpotlight}
                        showOnlyChinese={showOnlyChinese}
                        highlightNewWords={highlightNewWords}
                        vocabularySet={vocabularySet}
                        tokenizer={tokenizer}
                      />
                    )}
                  </div>
                  )}
                  
                  {/* Translation Text */}
                  {(viewMode === "chinese" || viewMode === "both") && (
                  <div className="space-y-2.5 sm:space-y-4 max-w-3xl mx-auto w-full">
                    {showChinese && (
                      <p className={cn(
                        "text-muted-foreground font-light leading-relaxed transition-all break-words [word-break:break-word]",
                        viewMode === "chinese" ? "text-xl sm:text-2xl md:text-3xl text-foreground" : "text-base sm:text-lg md:text-xl"
                      )}>
                        {currentSubtitle.chinese}
                      </p>
                    )}
                    {showRomaji && currentSubtitle.romaji && (
                      <p className="text-muted-foreground/60 text-sm sm:text-base md:text-lg font-mono break-all">
                        {currentSubtitle.romaji}
                      </p>
                    )}
                  </div>
                  )}
                </div>
              ) : (
              <div className="flex flex-col items-center justify-center text-muted-foreground/40 gap-4">
                <div className="w-16 h-16 rounded-full bg-muted/50 animate-pulse" />
                <p className="text-xl font-light italic">等待播放...</p>
              </div>
            )}
          </div>
          </div>

          {/* Controls Area */}
          <div className="flex flex-col items-center gap-3 sm:gap-4 p-2.5 sm:p-4 md:p-6 pt-2 shrink-0 bg-background/50 backdrop-blur-sm z-10 border-t border-border/5">
            {/* Recording Feedback */}
            <div className="h-8 w-full max-w-lg flex items-center justify-center relative">
              {isRecorderBusy && !isRecording ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  正在准备录音...
                </div>
              ) : isRecording ? (
                <div className="w-full bg-muted/30 rounded-full h-3 overflow-hidden relative shadow-inner">
                   {/* Center expanding bar */}
                   <div 
                      className="absolute top-0 bottom-0 left-1/2 bg-gradient-to-r from-green-400 to-green-600 transition-all duration-100 ease-linear -translate-x-1/2 shadow-[0_0_15px_rgba(34,197,94,0.6)]"
                      style={{ width: `${(recordingTime / MAX_RECORDING_TIME) * 100}%` }}
                   />
                   <div className="absolute right-0 -top-7 text-sm font-mono text-green-600 font-bold tracking-wider">
                      {recordingTime.toFixed(1)}s / {MAX_RECORDING_TIME}s
                   </div>
                </div>
              ) : audioUrl ? (
                <div className="flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2">
                  <div className="bg-green-500/10 text-green-600 px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 border border-green-500/20">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    录音完成，点击播放试听
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground/60 font-light tracking-wide">
                  {!currentSubtitle
                    ? "请先播放到有字幕的位置再录音"
                    : "点击麦克风开始录音（最长15秒）"}
                </div>
              )}
            </div>

            {audioUrl && isAppleTouchDevice && (
              <div className="w-full max-w-lg flex justify-center">
                <audio
                  className="w-full h-9"
                  src={audioUrl}
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
            )}

            {/* Main Buttons */}
            <div className="flex items-center justify-center gap-2.5 sm:gap-4 md:gap-8 lg:gap-12 w-full px-1 sm:px-4">
              {/* Loop Button */}
              {onToggleLoop && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onToggleLoop}
                  className={cn(
                    "h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full transition-all duration-300 shrink-0",
                    isLooping 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md scale-110" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  title={isLooping ? "关闭单句循环" : "开启单句循环"}
                >
                  <Repeat1 className="h-5 w-5" />
                </Button>
              )}

              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onPrev}
                className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full border-2 border-border/50 hover:border-primary/50 hover:bg-muted/50 hover:scale-110 transition-all duration-300"
              >
                <SkipBack className="h-5 w-5 text-muted-foreground" />
              </Button>

              <div className="flex items-center gap-2.5 sm:gap-4 md:gap-8">
                 {/* Playback Control (Original Audio) */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={onTogglePlay}
                  className="h-11 w-11 sm:h-12 sm:w-12 md:h-14 md:w-14 rounded-full border-2 border-primary/20 hover:border-primary hover:bg-primary/5 hover:scale-105 transition-all duration-300 shadow-sm"
                >
                  {isPlaying ? (
                    <Pause className="h-6 w-6 text-primary fill-current" />
                  ) : (
                    <Play className="h-6 w-6 text-primary fill-current ml-1" />
                  )}
                </Button>

                {/* Recording Control */}
                <div className="relative group">
                  {isRecording && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping duration-1000" />
                      <div className="absolute inset-0 rounded-full bg-red-500/10 animate-pulse duration-2000" />
                    </>
                  )}
                  <Button
                    type="button"
                    variant={isRecording ? "destructive" : "default"}
                    size="icon"
                    onClick={toggleRecording}
                    disabled={(isRecorderBusy && !isRecording) || (!currentSubtitle && !isRecording)}
                    className={cn(
                      "h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 rounded-full shadow-xl hover:scale-105 transition-all duration-300 relative z-10 border-4 border-background",
                      ((isRecorderBusy && !isRecording) || (!currentSubtitle && !isRecording)) && "opacity-70 cursor-not-allowed",
                      isRecording 
                        ? "bg-red-500 hover:bg-red-600 shadow-red-500/30" 
                        : "bg-gradient-to-br from-primary to-primary/80 hover:brightness-110 shadow-primary/30"
                    )}
                  >
                    {isRecorderBusy && !isRecording ? (
                      <Loader2 className="h-7 w-7 animate-spin" />
                    ) : isRecording ? (
                      <Square className="h-8 w-8 fill-current animate-in zoom-in duration-200" />
                    ) : (
                      <Mic className="h-8 w-8 animate-in zoom-in duration-200" />
                    )}
                  </Button>
                </div>

                {/* User Recording Playback */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={isPlayingRecording ? pauseRecording : playRecording}
                  disabled={!audioUrl || isRecording || isRecorderBusy}
                  className={cn(
                    "h-11 w-11 sm:h-12 sm:w-12 md:h-14 md:w-14 rounded-full border-2 transition-all duration-300 shadow-sm",
                    audioUrl && !isRecording
                      ? "border-green-500/50 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-500 hover:scale-105" 
                      : "opacity-40 cursor-not-allowed border-border bg-muted/20"
                  )}
                >
                   {isPlayingRecording ? (
                      <Pause className="h-6 w-6 fill-current" />
                   ) : (
                      <Volume2 className="h-6 w-6" />
                   )}
                </Button>
              </div>

              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onNext}
                className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full border-2 border-border/50 hover:border-primary/50 hover:bg-muted/50 hover:scale-110 transition-all duration-300"
              >
                <SkipForward className="h-5 w-5 text-muted-foreground" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {popupWord && (
        <WordPopup
          word={popupWord}
          position={popupPosition}
          onClose={() => setPopupWord(null)}
          onAddToVocabulary={handleAddToVocabulary}
        />
      )}
    </>,
    document.body,
  );
}
